import { forwardRef } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

// ----------------------------------------------------------------------

interface LabelProps {
  children: React.ReactNode;
  color?: 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';
  variant?: 'filled' | 'outlined' | 'ghost' | 'soft';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  sx?: any;
}

const StyledSpan = styled('span')<{ ownerState: { color: LabelProps['color']; variant: LabelProps['variant'] } }>(({ theme, ownerState }) => ({
  height: 24,
  minWidth: 22,
  lineHeight: 0,
  borderRadius: 6,
  cursor: 'default',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  justifyContent: 'center',
  textTransform: 'capitalize',
  padding: theme.spacing(0, 1),
  color: theme.palette.grey[800],
  fontSize: theme.typography.pxToRem(12),
  fontFamily: theme.typography.fontFamily,
  backgroundColor: theme.palette.grey[300],
  fontWeight: theme.typography.fontWeightBold,
  ...(ownerState.color === 'default' && {
    backgroundColor: theme.palette.grey[300],
  }),
  ...(ownerState.color !== 'default' && ownerState.color && {
    color: theme.palette[ownerState.color]?.contrastText,
    backgroundColor: theme.palette[ownerState.color]?.main,
  }),
}));

const Label = forwardRef<HTMLSpanElement, LabelProps>(({ 
  children, 
  color = 'default', 
  variant = 'soft', 
  startIcon, 
  endIcon, 
  sx, 
  ...other 
}, ref) => {
  const iconStyle = {
    width: 16,
    height: 16,
    '& svg, img': { width: 1, height: 1, objectFit: 'cover' },
  };

  return (
    <StyledSpan
      ref={ref}
      ownerState={{ color, variant }}
      sx={{
        ...(startIcon && { pl: 0.75 }),
        ...(endIcon && { pr: 0.75 }),
        ...sx
      }}
      {...other}
    >
      {startIcon && <Box sx={{ mr: 0.75, ...iconStyle }}>{startIcon}</Box>}
      {children}
      {endIcon && <Box sx={{ ml: 0.75, ...iconStyle }}>{endIcon}</Box>}
    </StyledSpan>
  );
});

Label.displayName = 'Label';

export default Label;
